import { useCallback, useMemo, useState } from 'react';

interface UseCarouselProps {
  totalSlides: number;
  initialSlide?: number;
}

interface UseCarouselReturn {
  currentSlide: number;
  goToSlide: (index: number) => void;
  nextSlide: () => void;
  previousSlide: () => void;
  isFirstSlide: boolean;
  isLastSlide: boolean;
}

export function useCarousel({ totalSlides, initialSlide = 0 }: UseCarouselProps): UseCarouselReturn {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSlides) {
        setCurrentSlide(index);
      }
    },
    [totalSlides]
  );

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const previousSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const isFirstSlide = useMemo(() => currentSlide === 0, [currentSlide]);
  const isLastSlide = useMemo(() => currentSlide === totalSlides - 1, [currentSlide, totalSlides]);

  return {
    currentSlide,
    goToSlide,
    nextSlide,
    previousSlide,
    isFirstSlide,
    isLastSlide
  };
}
